import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { verifyResponse } from "../../helpers/apiHelper";
import { postContribution, putContribution } from "../API/commentAPI";
import { getUserData } from "../API/profileAPI";

export const createContribution = createAsyncThunk(
  "item/createContribution",
  async (formData, thunkAPI) => {
    const state = thunkAPI.getState();
    const id = state.item.item.id;
    const externalId = state.item.item.externalId;
    const implementationId = state.item.item.implementationId;
    const name = state.item.item.name;
    const easting = state.item.item.location.easting;
    const northing = state.item.item.location.northing;
    const address = state.item.item.location.address;
    const itemPath = state.item.item.path;

    // add the item details to the post for the contribution.
    formData["itemId"] = id;
    formData["externalId"] = externalId;
    formData["implementationId"] = implementationId;
    formData["itemName"] = name;
    formData["address"] = address;
    formData["easting"] = easting;
    formData["northing"] = northing;
    formData["itemPath"] = itemPath;

    formData = ConvertFormDataStringsToArrays(formData);
    const response = await postContribution(formData);
    if(response.isValid !== undefined && !response.isValid){
      return verifyResponse(response, thunkAPI);
    }

    let userDataResponse = false;
    if (response.userId !== undefined && response.userId !== '00000000-0000-0000-0000-000000000000') {
      userDataResponse = await getUserData(response.userId);      
      return ({
          commentResponse: verifyResponse(response, thunkAPI),
          userResponse: verifyResponse(userDataResponse, thunkAPI)
        } 
      );
    }
    return {commentResponse: verifyResponse(response, thunkAPI), userResponse: null};
  }
);

export const updateContribution = createAsyncThunk(
  "item/updateContribution",
  async (formData, thunkAPI) => {
    const state = thunkAPI.getState();
    const id = state.item.item.id;
    // add the itemId to the contribution
    formData["itemId"] = id;

    formData = ConvertFormDataStringsToArrays(formData);

    const response = await putContribution(formData);

    return verifyResponse(response, thunkAPI);
  }
);

function ConvertFormDataStringsToArrays(formData) {
  //If only one image was uploaded, then the data is stored as a string,
  // but the API expects an array for each of these fields
  if (typeof formData["filenames"] === "string") {
    formData["filenames"] = [formData["filenames"]];
    formData["uploadUrls"] = [formData["uploadUrls"]];
    formData["ImageDescriptions"] = [formData["ImageDescriptions"]];
    formData["ImageCaptions"] = [formData["ImageCaptions"]];
  }
  return formData;
}

export const itemSlice = createSlice({
  name: "item",
  initialState: {
    user: {
      firstName: "",
      isOrganisation: false,
      lastName: "",
      memberId: "",
      organisationName: "",
    },
    top: 10, // how many contributions to return when calling the api
    skip: 0, // how many contributions to skip
    item: {
      id: 0,
      externalId: "0",
      comments: [],
      totalComments: 0,
      implementation: {
        active: true,
        moderationTimeInMinutes: 0,
        replyButtonText: "Reply",
      },
      location: {
        itemId: 0,
        easting: "",
        northing: "",
        address: ""
      },
    }, // currently available comments
    commentCount: 0, // total comment count
    pendingComments: [], // currently available pending comments
    pendingCommentCount: 0, // total pending comment count
    pendingCommentSubmission: false,
    pendingCommentSubmissionState: null,
    sortAscending: false,
    sortByStarred: false,
    showContributionsWithPhotos: false, 
    sortPinned: true,
    moreComments: false,
    reloadComments: false,
    errors: [],
  },
  reducers: {
    setUser(state, action) {
      return {
        ...state,
        user: action.payload,
      };
    },
    setReloadComments(state, action) {
      return {
        ...state,
        reloadComments: action.payload,
      };
    },
    setSortAscending(state, action) {
      //need to calculate how many comments to request rather then just checking the length of the comments array.
      let newTop = state.top + state.skip;
      return {
        ...state,
        item: {
          ...state.item,
          comments: [],
        }, 
        skip: 0,
        top: newTop,
        reloadComments: true,
        sortPinned : false,  
        sortAscending: action.payload
      };
    },
    setSortByStarred(state, action) {
      let newTop = state.top + state.skip;
      return {
        ...state,
        item: {
          ...state.item,
          comments: [],
        },
        skip: 0,
        top: newTop,
        sortPinned: false,
        reloadComments: true,  
        sortByStarred: action.payload
      };
    },
    setShowContributionsWithPhotos(state, action) {
      let newTop = state.top + state.skip;
      return {
        ...state,
        item: {
          ...state.item,
          comments: [],
        },
        skip: 0,
        top: newTop,
        sortPinned: false,
        reloadComments: true,  
        showContributionsWithPhotos: action.payload
      };
    },
    setSkip(state, action) {
      return {
        ...state,
        skip: action.payload,
      };
    },
    resetSkipAndTop(state) {
      return {
        ...state,
        item: {
          ...state.item,
          pendingComments: [],
          comments: [],
        },        
        skip: 0,
        top: 10,
        sortPinned: true
      };
    },
    setItem(state, action) {
      let comments = [];
      let moreComments = false;
      if (
        action.payload.comments &&
        action.payload.externalId === state.item.externalId
      ) {
        comments = [...state.item.comments];
        action.payload.comments.forEach((x) => {
          let foundIndex = comments.findIndex((y) => y.id === x.id);
          if (foundIndex === -1) {
            comments.push(x);
          }
        });

        moreComments = action.payload.comments.length === state.top;
      } else if (
        action.payload.comments &&
        action.payload.externalId !== state.item.externalId
      ) {
        comments = action.payload.comments;
        moreComments = action.payload.comments.length === state.top;
      }
     
      return {
        ...state,
        moreComments,
        reloadComments: false,
        
        item: {
          ...action.payload,
          comments: comments,         
        },
      };
    },
    setPendingCommentSubmissionState(state, action) {
      return {
        ...state,
        pendingCommentSubmissionState: action.payload,
      };
    },
    setErrors(state, action) {
      return {
        ...state,
        errors: action.payload,
      };
    },
    dismissError(state, action) {
      const newErrors = [];
      return { ...state, errors: newErrors };
    },
  },
  extraReducers: {
    [createContribution.pending]: (state) => {
      state.pendingCommentSubmission = true;
      return state;
    },
    [createContribution.rejected]: (state, action) => {
      state.pendingCommentSubmission = false;
      state.pendingCommentSubmissionState = "fail";
      state.errors = action.payload;
      return state;
    },
    [createContribution.fulfilled]: (state, action) => {
      state.pendingCommentSubmission = false;
      state.pendingCommentSubmissionState = "success";
      state.user = action.payload.userResponse !== null ? action.payload.userResponse : state.user;     
      return state;
    },
    [updateContribution.pending]: (state) => {
      state.pendingCommentSubmission = true;
      return state;
    },
    [updateContribution.rejected]: (state, action) => {
      state.pendingCommentSubmission = false;
      state.pendingCommentSubmissionState = "fail";
      state.errors = action.payload;
      return state;
    },
    [updateContribution.fulfilled]: (state, action) => {
      state.pendingCommentSubmission = false;
      state.pendingCommentSubmissionState = "success";
      return state;
    },
  },
});

const user = (state) => {
  return state.item.user;
};

const comments = (state) => {
  return state.item.comments;
};

const sortAscending = (state) => {
  return state.item.sortAscending;
};

const sortByStarred = (state) => {
  return state.item.sortByStarred;
};

const showContributionsWithPhotos = (state) => {
  return state.item.showContributionsWithPhotos;
}

const sortPinned = (state) => {
  return state.item.sortPinned;
}

const skip = (state) => {
  return state.item.skip;
};

const top = (state) => {
  return state.item.top;
};

const item = (state) => {
  return state.item.item;
};

const maxChars = (state) => {
  return state.item.item.implementation?.characterLimit;
};

const pendingCommentSubmission = (state) => {
  return state.item.pendingCommentSubmission;
};

const pendingCommentSubmissionState = (state) => {
  return state.item.pendingCommentSubmissionState;
};

const replyButtonText = (state) => {
  return state.item.item.implementation.replyButtonText;
};

const moreComments = (state) => {
  return state.item.moreComments;
};

const reloadComments = (state) => {
  return state.item.reloadComments;
};

const errors = (state) => {
  return state.item.errors;
};

const totalComments = (state) => {
  return state.item.item.totalComments;
};

export const selectors = {
  comments,
  skip,
  top,
  item,
  maxChars,
  pendingCommentSubmission,
  pendingCommentSubmissionState,
  replyButtonText,
  sortByStarred,
  sortAscending,
  showContributionsWithPhotos,
  sortPinned,
  moreComments,
  reloadComments,
  totalComments,
  errors,
  user,
};

export const {
  setSkip,
  setItem,
  setUser,
  resetSkipAndTop,
  resetItem,
  setPendingCommentSubmissionState,
  setSortAscending,
  setSortByStarred,
  setShowContributionsWithPhotos,
  setReloadComments,
  dismissError,
} = itemSlice.actions;

export default itemSlice.reducer;
